import React, { useState, useEffect, useMemo } from 'react';
import { apiCall } from '../config/api';
import LoadingSpinner from '../components/LoadingSpinner';

interface Prova {
  data: string;
  dataOriginal: string;
  nomeClube: string;
  responsavel: string;
  email: string;
  anulada: boolean;
  tipoProva: string;
  local: string;
  indoorOutdoor: string;
  condicoes: string;
  juiz: string;
  tipoPiso: string;
  tipoObstaculosContactos?: string;
  materialPneu?: string;
  materialSaltoComprimento?: string;
  materialMuro?: string;
  materialSaltos?: string;
}

const TIPOS_PROVA_OPCOES = [
  { value: '', label: 'Todos os tipos' },
  { value: 'Campeonato', label: 'Campeonato' },
  { value: 'Open', label: 'Open' },
  { value: 'Joker', label: 'Joker' },
  { value: 'Seletiva', label: 'Seletiva' },
  { value: 'Taça de Portugal', label: 'Taça de Portugal' },
];

const MAX_DAYS_TO_GROUP = 3;

type FieldKey = 'nomeClube' | 'anulada' | 'indoorOutdoor' | 'tipoProva' | 'local' | 'tipoPiso' | 'juiz' | 'responsavel' | 'email';

const FIELD_LABELS: Record<FieldKey, string> = {
  nomeClube: 'Clube',
  anulada: 'Anulada',
  indoorOutdoor: '',
  tipoProva: 'Tipo de prova',
  local: 'Local',
  tipoPiso: 'Tipo de piso',
  juiz: 'Juiz',
  responsavel: 'Responsável',
  email: 'Email',
};

function getFieldValue(p: Prova, key: FieldKey): string {
  if (key === 'anulada') return p.anulada ? 'Sim' : 'Não';
  const v = (p as unknown as Record<string, unknown>)[key];
  return (v != null && String(v).trim() !== '') ? String(v).trim() : '';
}

/** Normaliza para comparação: apenas letras e números, sem acentos, case-insensitive (evita separar por espaços, hífens, etc.). */
function normalizeForCompare(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9]/g, '');
}

/** Normaliza para filtros: trim, colapsar espaços, ignorar espaços à volta de / e parêntesis, case-insensitive. */
function normalizeForFilter(s: string): string {
  if (!s || typeof s !== 'string') return '';
  return s
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\s*\/\s*/g, '/')
    .replace(/\s*\(\s*/g, '(')
    .replace(/\s*\)\s*/g, ')')
    .toLowerCase();
}

/** Agrupa valores por chave normalizada e devolve um valor de display por grupo (primeiro encontrado), ordenado. */
function uniqueByNormalizedFilter(values: string[]): string[] {
  const byKey = new Map<string, string>();
  values.forEach((v) => {
    const trimmed = v.trim();
    if (!trimmed) return;
    const key = normalizeForFilter(trimmed);
    if (!byKey.has(key)) byKey.set(key, trimmed);
  });
  return Array.from(byKey.values()).sort((a, b) => a.localeCompare(b, 'pt-PT'));
}

/** Verifica se duas datas (YYYY-MM-DD) são dias consecutivos (b é o dia a seguir a a). */
function isNextDay(prev: string, curr: string): boolean {
  const d1 = new Date(prev + 'T12:00:00').getTime();
  const d2 = new Date(curr + 'T12:00:00').getTime();
  const diff = Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
  return diff === 1;
}

interface ProvaGroup {
  provas: Prova[];
  common: Partial<Record<FieldKey, string>>;
  differing: Array<{ data: string; dataShort: string; values: Record<FieldKey, string> }>;
}

const CalendarProvasPage: React.FC = () => {
  const [provas, setProvas] = useState<Prova[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPastProvas, setShowPastProvas] = useState(false);
  const [filterMes, setFilterMes] = useState('');
  const [filterClube, setFilterClube] = useState('');
  const [filterLocal, setFilterLocal] = useState('');
  const [filterTipoProva, setFilterTipoProva] = useState('');
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);
  const [modalGroup, setModalGroup] = useState<ProvaGroup | null>(null);

  useEffect(() => {
    loadProvas();
  }, []);

  const loadProvas = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiCall('/calendar-provas', { method: 'GET' });
      setProvas(res.provas || []);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar calendário de provas.');
      setProvas([]);
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  // Lista de meses únicos (ano-mês) para o filtro, orden cronológica (mais antigo primeiro)
  const mesesDisponiveis = useMemo(() => {
    const set = new Set<string>();
    provas.forEach((p) => {
      if (p.data && p.data.length >= 7) set.add(p.data.substring(0, 7));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [provas]);

  // Nomes de clubes únicos (agrupados por normalização: espaços, maiúsculas, etc.), ordenados
  const clubesDisponiveis = useMemo(() => {
    const raw = provas.map((p) => p.nomeClube || '').filter((s) => s.trim());
    return uniqueByNormalizedFilter(raw);
  }, [provas]);

  // Locais únicos (agrupados por normalização: espaços, maiúsculas, etc.), ordenados
  const locaisDisponiveis = useMemo(() => {
    const raw = provas.map((p) => p.local || '').filter((s) => s.trim());
    return uniqueByNormalizedFilter(raw);
  }, [provas]);

  // Aplicar filtros e visibilidade de provas passadas
  const filteredProvas = useMemo(() => {
    let list = [...provas];
    if (!showPastProvas) {
      list = list.filter((p) => p.data >= today);
    }
    if (filterMes) {
      list = list.filter((p) => p.data.startsWith(filterMes));
    }
    if (filterClube) {
      const keyClube = normalizeForFilter(filterClube);
      list = list.filter((p) => normalizeForFilter(p.nomeClube || '') === keyClube);
    }
    if (filterLocal) {
      const keyLocal = normalizeForFilter(filterLocal);
      list = list.filter((p) => normalizeForFilter(p.local || '') === keyLocal);
    }
    if (filterTipoProva) {
      const q = normalizeForFilter(filterTipoProva);
      list = list.filter((p) => normalizeForFilter(p.tipoProva || '').includes(q));
    }
    return list;
  }, [provas, showPastProvas, filterMes, filterClube, filterLocal, filterTipoProva, today]);

  const hasPastProvas = useMemo(() => provas.some((p) => p.data < today), [provas, today]);

  const hasActiveFilters = !!(filterMes || filterClube || filterLocal || filterTipoProva);

  const closeFiltersMobile = () => setShowFiltersMobile(false);

  // Formatação curta para sub-blocos (dia + mês)
  const formatDayShort = (data: string) => {
    const [y, m, d] = data.split('-');
    const date = new Date(parseInt(y!, 10), parseInt(m!, 10) - 1, parseInt(d!, 10));
    return date.toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' });
  };

  // Agrupar provas: mesmo clube + dias consecutivos (máx. 3). Não colapsar por data:
  // várias provas na mesma data (club differentes) ficam em blocos separados e todas são mostradas.
  const groups = useMemo((): ProvaGroup[] => {
    const list = [...filteredProvas].sort((a, b) => {
      const byClub = (a.nomeClube || '').localeCompare(b.nomeClube || '', 'pt-PT');
      if (byClub !== 0) return byClub;
      return a.data.localeCompare(b.data);
    });
    if (list.length === 0) return [];

    const keys: FieldKey[] = ['nomeClube', 'anulada', 'indoorOutdoor', 'tipoProva', 'local', 'tipoPiso', 'juiz', 'responsavel', 'email'];

    function buildGroup(provas: Prova[]): ProvaGroup {
      const common: Partial<Record<FieldKey, string>> = {};
      const differing = provas.map((p) => ({
        data: p.data,
        dataShort: formatDayShort(p.data),
        values: keys.reduce((acc, k) => ({ ...acc, [k]: getFieldValue(p, k) }), {} as Record<FieldKey, string>),
      }));

      keys.forEach((key) => {
        const first = getFieldValue(provas[0], key);
        const firstNorm = normalizeForCompare(first);
        const allSame = firstNorm === '' || provas.every((p) => normalizeForCompare(getFieldValue(p, key)) === firstNorm);
        if (allSame && first !== '') {
          common[key] = first;
        }
      });
      return { provas, common, differing };
    }

    const result: ProvaGroup[] = [];
    let current: Prova[] = [list[0]];
    const sameClub = (a: Prova, b: Prova) => normalizeForCompare(a.nomeClube || '') === normalizeForCompare(b.nomeClube || '');

    for (let i = 1; i < list.length; i++) {
      const prev = current[current.length - 1];
      const curr = list[i];
      const canExtend =
        sameClub(prev, curr) &&
        current.length < MAX_DAYS_TO_GROUP &&
        isNextDay(prev.data, curr.data);
      if (canExtend) {
        current.push(curr);
      } else {
        result.push(buildGroup(current));
        current = [curr];
      }
    }
    result.push(buildGroup(current));
    // Ordenar grupos: primeiro por data, depois por clube (várias provas no mesmo dia = vários blocos visíveis)
    result.sort((g1, g2) => {
      const byDate = g1.provas[0].data.localeCompare(g2.provas[0].data);
      if (byDate !== 0) return byDate;
      return (g1.provas[0].nomeClube || '').localeCompare(g2.provas[0].nomeClube || '', 'pt-PT');
    });
    return result;
  }, [filteredProvas]);

  const formatDate = (data: string) => {
    const [y, m, d] = data.split('-');
    const date = new Date(parseInt(y!, 10), parseInt(m!, 10) - 1, parseInt(d!, 10));
    return date.toLocaleDateString('pt-PT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatDateShort = (data: string) => {
    const [y, m, d] = data.split('-');
    const date = new Date(parseInt(y!, 10), parseInt(m!, 10) - 1, parseInt(d!, 10));
    return date.toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  /** Label curto para o canto do card: "14 Fev" ou "14 a 15 Fev" (sem ano). */
  const formatDateLabel = (data: string) => {
    const [y, m, d] = data.split('-');
    const date = new Date(parseInt(y!, 10), parseInt(m!, 10) - 1, parseInt(d!, 10));
    const monthShort = date.toLocaleDateString('pt-PT', { month: 'short' }).replace(/\.$/, '');
    const monthCap = monthShort.charAt(0).toUpperCase() + monthShort.slice(1);
    return `${date.getDate()} ${monthCap}`;
  };

  const formatMonthOption = (ym: string) => {
    const [y, m] = ym.split('-');
    const date = new Date(parseInt(y!, 10), parseInt(m!, 10) - 1, 1);
    return date.toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' });
  };

  const formatDateRange = (first: string, last: string) => {
    const [y1, m1, d1] = first.split('-');
    const [y2, m2, d2] = last.split('-');
    const dateFirst = new Date(parseInt(y1!, 10), parseInt(m1!, 10) - 1, parseInt(d1!, 10));
    const dateLast = new Date(parseInt(y2!, 10), parseInt(m2!, 10) - 1, parseInt(d2!, 10));
    const monthYear = dateLast.toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' });
    return `de ${dateFirst.getDate()} a ${dateLast.getDate()} de ${monthYear}`;
  };

  const formatDateRangeShort = (first: string, last: string) => {
    const [y1, m1, d1] = first.split('-');
    const [y2, m2, d2] = last.split('-');
    const dateFirst = new Date(parseInt(y1!, 10), parseInt(m1!, 10) - 1, parseInt(d1!, 10));
    const dateLast = new Date(parseInt(y2!, 10), parseInt(m2!, 10) - 1, parseInt(d2!, 10));
    const monthShort = dateLast.toLocaleDateString('pt-PT', { month: 'short' }).replace(/\.$/, '');
    const monthCapitalized = monthShort.charAt(0).toUpperCase() + monthShort.slice(1);
    return `${dateFirst.getDate()} a ${dateLast.getDate()} ${monthCapitalized}`;
  };

  if (loading) {
    return (
      <div className="container py-4">
        <div className="text-center py-5">
          <LoadingSpinner />
          <p className="text-muted mt-2">A carregar calendário de provas...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning shadow-sm">
          <h5 className="alert-heading">
            <i className="fas fa-calendar-alt me-2"></i>
            Calendário de Provas
          </h5>
          <p className="mb-0">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="container py-3 py-md-4" style={{ paddingBottom: '3rem' }}>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 mb-md-4">
        <h4 className="mb-0" style={{ color: '#a2449c' }}>
          <i className="fas fa-calendar-alt me-2"></i>
          Calendário de Provas
        </h4>
      </div>

      {/* Filtros: no mobile colapsável, no desktop sempre visível */}
      <div className="card shadow-sm mb-4">
        <div
          className="card-header py-2 d-md-none"
          style={{ cursor: 'pointer' }}
          onClick={() => setShowFiltersMobile((v) => !v)}
          role="button"
          aria-expanded={showFiltersMobile}
          aria-label={showFiltersMobile ? 'Ocultar filtros' : 'Mostrar filtros'}
        >
          <h6 className="mb-0 text-muted d-flex align-items-center">
            <i className="fas fa-filter me-2"></i>
            Filtros
            {hasActiveFilters && (
              <i
                className="fas fa-exclamation-circle ms-2"
                style={{ color: '#e65100' }}
                title="Filtros aplicados"
                aria-hidden="true"
              ></i>
            )}
            <i className={`fas fa-chevron-${showFiltersMobile ? 'up' : 'down'} ms-auto mt-1`}></i>
          </h6>
        </div>
        <div className={`card-body p-3 ${showFiltersMobile ? '' : 'd-none'} d-md-block`}>
          <h6 className="text-muted mb-3 d-none d-md-flex align-items-center">
            <i className="fas fa-filter me-2"></i>
            Filtros
            {hasActiveFilters && (
              <i className="fas fa-exclamation-circle ms-2" style={{ color: '#e65100' }} title="Filtros aplicados"></i>
            )}
          </h6>
          <div className="row g-2 g-md-3">
            <div className="col-12 col-sm-6 col-md-3">
              <label className="form-label small text-muted mb-1">Mês</label>
              <select
                className="form-select form-select-sm"
                value={filterMes}
                onChange={(e) => { setFilterMes(e.target.value); closeFiltersMobile(); }}
              >
                <option value="">Todos os meses</option>
                {mesesDisponiveis.map((ym) => (
                  <option key={ym} value={ym}>
                    {formatMonthOption(ym)}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-6 col-md-3">
              <label className="form-label small text-muted mb-1">Nome do clube</label>
              <select
                className="form-select form-select-sm"
                value={filterClube}
                onChange={(e) => { setFilterClube(e.target.value); closeFiltersMobile(); }}
              >
                <option value="">Todos os clubes</option>
                {clubesDisponiveis.map((nome) => (
                  <option key={nome} value={nome}>
                    {nome}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-6 col-md-3">
              <label className="form-label small text-muted mb-1">Local da prova</label>
              <select
                className="form-select form-select-sm"
                value={filterLocal}
                onChange={(e) => { setFilterLocal(e.target.value); closeFiltersMobile(); }}
              >
                <option value="">Todos os locais</option>
                {locaisDisponiveis.map((local) => (
                  <option key={local} value={local}>
                    {local}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-6 col-md-3">
              <label className="form-label small text-muted mb-1">Tipo de prova</label>
              <select
                className="form-select form-select-sm"
                value={filterTipoProva}
                onChange={(e) => { setFilterTipoProva(e.target.value); closeFiltersMobile(); }}
              >
                {TIPOS_PROVA_OPCOES.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Botão Ver / Ocultar provas anteriores - full width no mobile */}
      {hasPastProvas && (
        <div className="mb-3">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm btn-provas-anteriores"
            onClick={() => setShowPastProvas((v) => !v)}
          >
            <i className={`fas fa-${showPastProvas ? 'eye-slash' : 'history'} me-1`}></i>
            {showPastProvas ? 'Ocultar provas anteriores' : 'Ver provas anteriores'}
          </button>
        </div>
      )}

      {filteredProvas.length === 0 ? (
        <div className="card shadow-sm">
          <div className="card-body text-center py-5">
            <i className="fas fa-calendar-times fa-3x mb-3" style={{ color: '#a2449c' }}></i>
            <p className="text-muted mb-0">
              {provas.length === 0
                ? 'Nenhuma prova encontrada com data e clube preenchidos.'
                : 'Nenhuma prova corresponde aos filtros selecionados.'}
            </p>
          </div>
        </div>
      ) : (
        <div className="row g-3">
          {groups.map((group, groupIdx) => {
            const isSingle = group.provas.length === 1;
            const first = group.provas[0];
            const last = group.provas[group.provas.length - 1];
            const keys: FieldKey[] = ['nomeClube', 'anulada', 'indoorOutdoor', 'tipoProva', 'local', 'tipoPiso', 'juiz', 'responsavel', 'email'];

            const year = first.data.substring(0, 4);
            const dateLabel = isSingle ? formatDateLabel(first.data) : formatDateRangeShort(first.data, last.data);

            return (
              <div key={`group-${first.data}-${first.nomeClube}-${groupIdx}`} className="col-12">
                <div
                  className="card shadow-sm h-100"
                  role="button"
                  tabIndex={0}
                  onClick={() => setModalGroup(group)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setModalGroup(group); } }}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="card-body p-3 p-md-4">
                    <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                      <div className="d-flex align-items-center flex-wrap gap-2 min-w-0">
                        <span className="fw-bold fs-6" style={{ color: '#a2449c' }}>
                          {isSingle ? first.nomeClube : (group.common.nomeClube || first.nomeClube)}
                        </span>
                        {isSingle && first.anulada && (
                          <span className="badge bg-danger">Anulada</span>
                        )}
                        {!isSingle && group.common.anulada === 'Sim' && (
                          <span className="badge bg-danger">Anulada</span>
                        )}
                        {(isSingle ? first.indoorOutdoor : group.common.indoorOutdoor) && (
                          <span className="badge bg-light text-dark border">
                            {isSingle ? first.indoorOutdoor : group.common.indoorOutdoor}
                          </span>
                        )}
                      </div>
                      <div className="text-end flex-shrink-0">
                        <div className="small text-uppercase fw-bold text-secondary">{dateLabel}</div>
                        <div className="small text-muted" style={{ fontSize: '0.75rem' }}>{year}</div>
                      </div>
                    </div>
                    <div className="row g-2 g-md-3">
                      <div className="col-12">
                        {isSingle ? (
                          <>
                            {first.tipoProva && (
                              <div className="small text-muted mb-1">{first.tipoProva}</div>
                            )}
                            <div className="row g-2 small mt-2">
                              {first.local && (
                                <div className="col-12 col-sm-6 col-md-4">
                                  <i className="fas fa-map-marker-alt me-1 text-muted"></i>
                                  <span>{first.local}</span>
                                </div>
                              )}
                              {first.tipoPiso && (
                                <div className="col-12 col-sm-6 col-md-4">
                                  <i className="fas fa-layer-group me-1 text-muted"></i>
                                  <span>Tipo de piso: {first.tipoPiso}</span>
                                </div>
                              )}
                              {first.juiz && (
                                <div className="col-12 col-sm-6 col-md-4">
                                  <i className="fas fa-gavel me-1 text-muted"></i>
                                  <span>Juiz: {first.juiz}</span>
                                </div>
                              )}
                            </div>
                            <div className="small mt-2 text-muted">
                              <i className="fas fa-user me-1"></i>
                              {first.responsavel}
                              {first.email && (
                                <>
                                  {' · '}
                                  <a href={`mailto:${first.email}`}>{first.email}</a>
                                </>
                              )}
                            </div>
                          </>
                        ) : (
                          <>
                            {group.common.tipoProva && (
                              <div className="small text-muted mb-1">{group.common.tipoProva}</div>
                            )}

                            <div className="row g-2 small mt-2">
                              {group.common.local && (
                                <div className="col-12 col-sm-6 col-md-4">
                                  <i className="fas fa-map-marker-alt me-1 text-muted"></i>
                                  <span>{group.common.local}</span>
                                </div>
                              )}
                              {group.common.tipoPiso && (
                                <div className="col-12 col-sm-6 col-md-4">
                                  <i className="fas fa-layer-group me-1 text-muted"></i>
                                  <span>Tipo de piso: {group.common.tipoPiso}</span>
                                </div>
                              )}
                              {group.common.juiz && (
                                <div className="col-12 col-sm-6 col-md-4">
                                  <i className="fas fa-gavel me-1 text-muted"></i>
                                  <span>Juiz: {group.common.juiz}</span>
                                </div>
                              )}
                            </div>

                            {(group.common.responsavel || group.common.email) && (
                              <div className="small mt-2 text-muted">
                                <i className="fas fa-user me-1"></i>
                                {group.common.responsavel}
                                {group.common.email && (
                                  <>
                                    {' · '}
                                    <a href={`mailto:${group.common.email}`}>{group.common.email}</a>
                                  </>
                                )}
                              </div>
                            )}

                            {/* Sub-blocos para campos que diferem entre dias */}
                            {keys.map((key) => {
                              if (group.common[key] !== undefined) return null;
                              if (key === 'email' && group.common.responsavel === undefined) return null;
                              const hasAny = group.differing.some((d) => d.values[key] !== '');
                              if (!hasAny) return null;
                              const label = key === 'indoorOutdoor' ? 'Indoor/Outdoor' : (FIELD_LABELS[key] || key);
                              return (
                                <div key={key} className="mt-2 small">
                                  <div className="text-muted fw-bold mb-1">{label}</div>
                                  <div className="ps-2 border-start border-2" style={{ borderColor: '#dee2e6' }}>
                                    {group.differing.map((d) => {
                                      const v = d.values[key];
                                      if (v === '') return null;
                                      if (key === 'email') {
                                        return (
                                          <div key={d.data} className="mb-1">
                                            <span className="text-muted">{d.dataShort}:</span>{' '}
                                            <a href={`mailto:${v}`}>{v}</a>
                                          </div>
                                        );
                                      }
                                      if (key === 'responsavel') {
                                        const emailVal = d.values.email;
                                        return (
                                          <div key={d.data} className="mb-1">
                                            <span className="text-muted">{d.dataShort}:</span> {v}
                                            {emailVal && <><span> · </span><a href={`mailto:${emailVal}`}>{emailVal}</a></>}
                                          </div>
                                        );
                                      }
                                      return (
                                        <div key={d.data} className="mb-1">
                                          <span className="text-muted">{d.dataShort}:</span> {v}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>

    {modalGroup && (
      <div
        className="modal fade show d-block"
        tabIndex={-1}
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        onClick={() => setModalGroup(null)}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modalProvaLabel"
      >
        <div
          className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="modal-header border-0 pb-0">
              <h5 className="modal-title" id="modalProvaLabel" style={{ color: '#a2449c' }}>
                <i className="fas fa-info-circle me-2"></i>
                Detalhe da prova
              </h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Fechar"
                onClick={() => setModalGroup(null)}
              />
            </div>
            <div className="modal-body pt-0">
              {(() => {
                const formatDayShort = (data: string) => {
                  const [y, m, d] = data.split('-');
                  const date = new Date(parseInt(y!, 10), parseInt(m!, 10) - 1, parseInt(d!, 10));
                  return date.toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' });
                };
                const getProvaValue = (p: Prova, key: string): string => {
                  if (key === 'data') {
                    if (p.dataOriginal && p.dataOriginal.trim()) return p.dataOriginal.trim();
                    const [y, m, d] = p.data.split('-');
                    return new Date(parseInt(y!, 10), parseInt(m!, 10) - 1, parseInt(d!, 10)).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' });
                  }
                  if (key === 'anulada') return p.anulada ? 'Sim' : 'Não';
                  const v = (p as unknown as Record<string, unknown>)[key];
                  return (v != null && String(v).trim() !== '') ? String(v).trim() : '';
                };
                const MODAL_KEYS: { key: string; label: string }[] = [
                  { key: 'data', label: 'Data' },
                  { key: 'nomeClube', label: 'Nome do clube' },
                  { key: 'responsavel', label: 'Nome do responsável' },
                  { key: 'email', label: 'Email' },
                  { key: 'tipoProva', label: 'Nº e tipo de prova (Campeonato/Open/Seletiva)' },
                  { key: 'local', label: 'Local da prova' },
                  { key: 'indoorOutdoor', label: 'Indoor ou outdoor' },
                  { key: 'condicoes', label: 'Condições envolventes (WC, Água, Comida, Dormida)' },
                  { key: 'juiz', label: 'Juiz' },
                  { key: 'tipoPiso', label: 'Tipo de piso' },
                  { key: 'tipoObstaculosContactos', label: 'Tipo obstáculos contactos' },
                  { key: 'materialPneu', label: 'Material do pneu' },
                  { key: 'materialSaltoComprimento', label: 'Material do salto comprimento' },
                  { key: 'materialMuro', label: 'Material do muro' },
                  { key: 'materialSaltos', label: 'Material dos saltos' },
                ];
                const provas = modalGroup.provas;
                const isSingle = provas.length === 1;

                if (isSingle) {
                  const p = provas[0];
                  const dataFormatada = getProvaValue(p, 'data');
                  return (
                    <>
                      {p.anulada && (
                        <div className="mb-2">
                          <span className="badge bg-danger">Anulada</span>
                        </div>
                      )}
                      <dl className="row small mb-0">
                        {MODAL_KEYS.map(({ key, label }) => {
                          const value = getProvaValue(p, key);
                          if (!value) return null;
                          return (
                            <React.Fragment key={key}>
                              <dt className="col-sm-4 text-muted fw-normal">{label}</dt>
                              <dd className="col-sm-8 mb-2">
                                {key === 'email' ? <a href={`mailto:${value}`}>{value}</a> : value}
                              </dd>
                            </React.Fragment>
                          );
                        })}
                      </dl>
                    </>
                  );
                }

                const common: Record<string, string> = {};
                const differing: Record<string, Array<{ dataShort: string; value: string }>> = {};
                MODAL_KEYS.forEach(({ key }) => {
                  const values = provas.map((p) => getProvaValue(p, key));
                  const normalized = values.map((v) => normalizeForCompare(v));
                  const allSame = normalized.every((n) => n === normalized[0]) && normalized[0] !== '';
                  if (allSame && values[0]) {
                    common[key] = values[0];
                  } else {
                    differing[key] = provas.map((p) => ({
                      dataShort: formatDayShort(p.data),
                      value: getProvaValue(p, key),
                    })).filter((d) => d.value);
                  }
                });

                const hasAnulada = provas.some((p) => p.anulada);
                return (
                  <>
                    {hasAnulada && (
                      <div className="mb-2">
                        <span className="badge bg-danger">Anulada</span>
                      </div>
                    )}
                    {Object.keys(common).length > 0 && (
                      <div className="mb-3">
                        <div className="small text-muted mb-2">
                          De {getProvaValue(provas[0], 'data')} a {getProvaValue(provas[provas.length - 1], 'data')}
                        </div>
                        <dl className="row small mb-0">
                          {MODAL_KEYS.filter(({ key }) => common[key] && key !== 'data').map(({ key, label }) => (
                            <React.Fragment key={key}>
                              <dt className="col-sm-4 text-muted fw-normal">{label}</dt>
                              <dd className="col-sm-8 mb-2">
                                {key === 'email' ? (
                                  <a href={`mailto:${common[key]}`}>{common[key]}</a>
                                ) : (
                                  common[key]
                                )}
                              </dd>
                            </React.Fragment>
                          ))}
                        </dl>
                      </div>
                    )}
                    {MODAL_KEYS.some(({ key }) => key !== 'data' && differing[key]?.length) && (
                      <div>
                        <h6 className="text-muted small text-uppercase fw-bold mb-2">Por data</h6>
                        {MODAL_KEYS.filter(({ key }) => key !== 'data' && differing[key]?.length).map(({ key, label }) => (
                          <div key={key} className="mb-3 small">
                            <div className="text-muted fw-bold mb-1">{label}</div>
                            <div className="ps-2 border-start border-2" style={{ borderColor: '#dee2e6' }}>
                              {differing[key].map((d, i) => (
                                <div key={i} className="mb-1">
                                  <span className="text-muted">{d.dataShort}:</span>{' '}
                                  {key === 'email' ? (
                                    <a href={`mailto:${d.value}`}>{d.value}</a>
                                  ) : (
                                    d.value
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    )}

    <footer
      className="text-center py-2 border-top bg-light"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        fontSize: '0.75rem',
        color: '#6c757d',
        zIndex: 1020,
      }}
    >
      Esta informação é da total responsabilidade do CPC Agility
    </footer>
    </>
  );
};

export default CalendarProvasPage;
